import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { AdminDataService } from './admin-data.service';
import * as AdminActions from './admin.actions';

@Injectable()
export class AdminGeneralEffects {
  constructor(private actions$: Actions, private dataService: AdminDataService) {}

  getProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getSupportedProviders),
      switchMap(() =>
        this.dataService.getProviders().pipe(
          map((providers) => AdminActions.setSupportedProviders({ providers })),
          catchError(() => of(AdminActions.setSupportedProviders({ providers: null })))
        )
      )
    )
  );

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getUsers),
      switchMap(() =>
        this.dataService.getUsers().pipe(
          map((users) => AdminActions.setUsers({ users })),
          catchError((err) => of(AdminActions.setUsers({ users: null, error: err })))
        )
      )
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.createUser),
      switchMap((action) =>
        this.dataService.createUser(action.user, action.invite_id).pipe(
          map((user) => AdminActions.onUserCreated({ user: user.user })),
          catchError((err) => of(AdminActions.onUserCreated({ user: null, error: err })))
        )
      )
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.deleteUser),
      switchMap((action) =>
        this.dataService.deleteUser(action.user).pipe(
          map((users) => AdminActions.setUsers({ users })),
          catchError((err) => of(AdminActions.setUsers({ users: null, error: err })))
        )
      )
    )
  );

  getPermissionsList = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getPermissionsList),
      switchMap(() =>
        this.dataService.getPermissionsList().pipe(
          map((permissions) => AdminActions.setPermissionsList({ permissions })),
          catchError((error) => of(AdminActions.setPermissionsList({ permissions: null, error })))
        )
      )
    )
  );

  getPermissionGroups = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getPermissionGroups),
      switchMap(() =>
        this.dataService.getPermissionGroups().pipe(
          map((permissionGroups) => AdminActions.setPermissionGroups({ permissionGroups })),
          catchError((error) =>
            of(AdminActions.setPermissionGroups({ permissionGroups: null, error }))
          )
        )
      )
    )
  );

  getMemberCounts = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getMemberCounts),
      switchMap(() =>
        this.dataService.getMemberCounts().pipe(
          map((memberCounts) => AdminActions.setMemberCounts({ memberCounts })),
          catchError((error) => of(AdminActions.setMemberCounts({ memberCounts: null, error })))
        )
      )
    )
  );

  createPermissionGroup = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.createPermissionGroup),
      switchMap((action) =>
        this.dataService.createPermissionGroup(action).pipe(
          map((permissionGroups) => AdminActions.setPermissionGroups({ permissionGroups })),
          catchError((error) =>
            of(AdminActions.setPermissionGroups({ permissionGroups: null, error }))
          )
        )
      )
    )
  );

  deletePermissionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.deletePermissionGroup),
      switchMap((action) =>
        this.dataService.deletePermissionGroup(action.id).pipe(
          concatMap(() => {
            return [AdminActions.getPermissionGroups(), AdminActions.getUsers()];
          }),
          catchError((error) =>
            of(AdminActions.setPermissionGroups({ permissionGroups: null, error }))
          )
        )
      )
    )
  );

  addUserToGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.updateUserGroups),
      switchMap((action) =>
        this.dataService.updateUserGroups(action.user, action.groups, action.editor_email).pipe(
          map((users) => AdminActions.setUsers({ users })),
          catchError((error) => of(AdminActions.setUsers({ users: null, error })))
        )
      )
    )
  );

  getInvites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getInvites),
      switchMap(() =>
        this.dataService.getInvites().pipe(
          map((invites) => AdminActions.setInvites({ invites })),
          catchError((error) => of(AdminActions.setInvites({ invites: null, error })))
        )
      )
    )
  );

  createInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.createInvite),
      switchMap((action) =>
        this.dataService.createInvite(action.invite).pipe(
          map(() => AdminActions.getInvites()),
          catchError((error) => of(AdminActions.setInvites({ invites: null, error })))
        )
      )
    )
  );

  deleteInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.deleteInvite),
      switchMap((action) =>
        this.dataService.deleteInvite(action.invite_id).pipe(
          map(() => AdminActions.getInvites()),
          catchError((error) => of(AdminActions.setInvites({ invites: null, error })))
        )
      )
    )
  );

  getBillingDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getBillingDetails),
      switchMap(() =>
        this.dataService.getBillingDetails().pipe(
          map((billingDetails) => AdminActions.setBillingDetails({ billingDetails })),
          catchError((error) => of(AdminActions.setBillingDetails({ billingDetails: null, error })))
        )
      )
    )
  );

  getBillingCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getBillingCard),
      switchMap(() =>
        this.dataService.getBillingCard().pipe(
          map((card) => AdminActions.setBillingCard({ card })),
          catchError((error) => of(AdminActions.setBillingCard({ card: null, error })))
        )
      )
    )
  );

  getSubscriptionInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getSubscriptionInvoices),
      switchMap((action) =>
        this.dataService.getSubscriptionInvoices(action.pageIndex, action.pageSize).pipe(
          map((invoices) => AdminActions.setSubscriptionInvoices({ invoices })),
          catchError((error) => of(AdminActions.setSubscriptionInvoices({ invoices: null, error })))
        )
      )
    )
  );

  getVehicleAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.getVehicleAccess),
      switchMap(({ email }) =>
        this.dataService.getVehicleAccess(email).pipe(
          map((vehicleAccess) =>
            AdminActions.setVehicleAccess({
              vehicle_ids: vehicleAccess ? vehicleAccess.map((v) => v.vehicle) : [],
            })
          ),
          catchError((error) =>
            of(AdminActions.setVehicleAccess({ vehicle_ids: [], error: error.message }))
          )
        )
      )
    )
  );

  setVehicleAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.setVehicleAccess),
      switchMap((action) =>
        this.dataService.setVehicleAccess(
          action.vehicle_ids,
          action.fleet_ids,
          action.allVehicles,
          action.email,
          action.user
        ).pipe(
          switchMap(() =>
            action.user ? [AdminActions.getVehicleAccess({ email: action.email })] : []
          ),
          catchError((error) =>
            of(AdminActions.setVehicleAccess({ vehicle_ids: [], error: error.message }))
          )
        )
      )
    )
  );
  deleteVehicleAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.deleteVehicleAccess),
      switchMap((action) =>
        this.dataService.deleteVehicleAccess(action.vehicle_id, action.user).pipe(
          switchMap(() =>
            action.user ? [AdminActions.getVehicleAccess({ email: action.user.email })] : []
          ),
          catchError((error) =>
            of(AdminActions.setVehicleAccess({ vehicle_ids: [], error: error.message }))
          )
        )
      )
    )
  );
}
